import { FunctionComponent } from 'react';

import { BkAccount } from 'ucl/themes/bk/icons/BKAccount';
import { BkCode } from 'ucl/themes/bk/icons/BKCode';
import { BkCrown } from 'ucl/themes/bk/icons/BKCrown';
import { BkCrownPass } from 'ucl/themes/bk/icons/BKCrownPass';
import { BkDaytime } from 'ucl/themes/bk/icons/BKDaytime';
import { BkOffers } from 'ucl/themes/bk/icons/BKOffers';
import { BkOrder } from 'ucl/themes/bk/icons/BKOrder';
import { BkRewards } from 'ucl/themes/bk/icons/BKRewards';
import { BkSaving } from 'ucl/themes/bk/icons/BKSaving';
import { BkSunrise } from 'ucl/themes/bk/icons/BKSunrise';
import { BkUpsellPlus, BkUpsellPlusV2 } from 'ucl/themes/bk/icons/BKUpsellPlus';

import type * as SVGIcons from '../../themes/icons';
import type * as SVGLogos from '../../themes/logos';
import { getObjectKeys } from '../../utils/js';
import {  SvgProps, registerSVG, svgLibrary } from '../svg';

export type AddPrefix<TKey, TPrefix extends string> = TKey extends string
  // eslint-disable-next-line prettier/prettier
  ? `${TPrefix}${TKey}`
  : never;

/**
 * Adds a prefix to every string in an array
 */
const addPrefix = <T extends string, Prefix extends string>(list: T[], prefix: Prefix) =>
  list.map((item) => `${prefix}${item}`) as AddPrefix<T, Prefix>[];

/**
 * SVG Types combining icons and logos
 */
export type SVGTypes = typeof SVGIcons & typeof SVGLogos;

/**
 * Prefixed list of SVG Icon Names
 */
export type svgIconNames = AddPrefix<keyof SVGTypes, 'custom:'>;

/**
 * Prefixed list of Material Icon Names
 */
const materialIconNames = addPrefix(
  [
    'accessible',
    'add',
    'account-circle',
    'bug-report',
    'camera-alt',
    'close',
    'content-copy',
    'shopping-bag',
    'keyboard-arrow-up',
    'check',
    'check-circle-outline',
    'payment',
    'delivery-dining',
    'drive-eta',
    'edit',
    'error-outline',
    'error',
    'expand-more',
    'expand-less',
    'favorite-outline',
    'favorite',
    'forward',
    'help-outline',
    'home',
    'info-outline',
    'info',
    'language',
    'laptop',
    'list',
    'location-on',
    'lock',
    'menu',
    'monitor',
    'more-horiz',
    'more-vert',
    'near-me',
    'navigate-next',
    'local-offer',
    'qr-code',
    'qr-code-scanner',
    'refresh',
    'remove',
    'remove-circle-outline',
    'restaurant',
    'keyboard-arrow-down',
    'search',
    'settings',
    'star-border',
    'star',
    'store',
    'phone',
    'access-time',
    'lock-open',
    'visibility',
    'visibility-off',
    'keyboard-voice',
    'warning',
    'wallet-giftcard',
  ],
  'mi:',
);

// add custom svgs here
const customIcons : [svgKey: string, component: FunctionComponent<SvgProps>][] = [
  ['BkAccount', BkAccount],
  ['BkCode', BkCode],
  ['BkCrown', BkCrown],
  ['BkCrownPass', BkCrownPass],
  ['BkDaytime', BkDaytime],
  ['BkOffers', BkOffers],
  ['BkOrder', BkOrder],
  ['BkRewards', BkRewards],
  ['BkSaving', BkSaving],
  ['BkSunrise', BkSunrise],
  ['BkUpsellPlus', BkUpsellPlus],
  ['BkUpsellPlusV2', BkUpsellPlusV2],
];
customIcons.forEach(([name, component]) => {
  registerSVG(name, component);
});
const customIconNames =addPrefix(customIcons.map(([name]) => name), 'custom:')

export function getIconNames() {
  const customKeys = addPrefix(getObjectKeys(svgLibrary as unknown as SVGTypes), 'custom:');
  return [...customKeys, ...materialIconNames, ...customIconNames];
}

export type IconName = ReturnType<typeof getIconNames>[number];

/**
 * Icons with the NativeBase icon are finicky.
 *
 * Icons included in this list render within the NativeBase Icon `as` prop.
 *
 * This fixes an issue where using the SVG as children breaks some SVG features.
 *
 * This can only be used with Icons that come with their own colors that can't be
 * overridden.
 */
export const svgAsExclusions: readonly svgIconNames[] = ['custom:Jcb'];
